import { t } from "@lingui/macro";
import {
  OiBudgetItemsTimeBillingOrderByColumn,
  type OiBudgetItemsTimeBillingWhereColumn,
} from "@src/__generated__/graphql";
import {
  LockedTimeBillingColumn,
  TimeBillingsColumn,
} from "@src/components/modules/expenses/listing/expense-items/components/columns";
import {
  InvoiceNoColumn,
  type InvoiceNoColumnProps,
} from "@src/components/modules/invoices/outgoing/components/columns";
import {
  BudgetItemColumn,
  OptionsToLinks,
} from "@src/components/modules/invoices/outgoing/invoice-items/components/columns";
import { PaidAtCell } from "@src/components/modules/invoices/shared/columns";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  DataTable,
  LoadingOverlay,
  TruncatedTextWithTooltip,
} from "@src/components/ui-kit";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { appStore } from "@src/stores/AppStore";
import { FiltersPanel } from "@src/utils/components/filters/components/filters-panel";
import { currency, formatDate } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { useUpdateURI } from "@src/utils/hooks/useUpdateURI";
import mapToOptions from "@src/utils/map-to-options";
import { observer, useLocalObservable } from "mobx-react-lite";
import { Fragment } from "react";
import {
  type OutgoingInvoiceItemsTableItem,
  StoreOutgoingInvoiceItems,
} from "./store";

export const OutgoingInvoicesItemsList = observer(
  function OutgoingInvoicesItemsList() {
    const { workspaceStore } = useStore();
    const { store, columns } = useLocalObservable(() => ({
      store: new StoreOutgoingInvoiceItems(),
      columns: getColumns(workspaceStore.companies.length <= 1),
    }));

    useUpdateURI(store.searchParams);

    return (
      <Fragment>
        <DataTable.Filter>
          <FiltersPanel<OiBudgetItemsTimeBillingWhereColumn>
            filters={store.where}
            searchPlaceholder={t`Search by invoice no, brand, project, budget item`}
            onSearch={store.setSearchTerm}
            searchDefaultValue={store.searchTerm}
          />
        </DataTable.Filter>

        <DataTable.SummaryBox hideBg>
          <DataTable.SummaryItem
            label={t`Count`}
            isLoaded={!store.isSummaryLoading}
          >
            {store.summary?.count ?? 0}
          </DataTable.SummaryItem>
          <DataTable.SummaryItem
            label={t`Total amount`}
            isLoaded={!store.isSummaryLoading}
          >
            {currency.formatByWorkspace(store.summary?.amount ?? 0)}
          </DataTable.SummaryItem>
        </DataTable.SummaryBox>

        <LoadingOverlay isLoading={store.isLoading} position="absolute">
          <DataTable.Body
            tableKey={store.tableKey}
            data={store.tableData}
            columns={columns}
            orderedBy={store.orderBy.clauses[0]}
            onOrderByChange={store.handleOrderChange}
          />
        </LoadingOverlay>

        <Pagination store={store.pagination} />
      </Fragment>
    );
  },
);

function getColumns(
  hideLegalEntity: boolean,
): BodyColumnDef<
  OutgoingInvoiceItemsTableItem,
  OiBudgetItemsTimeBillingOrderByColumn
>[] {
  return [
    {
      id: "invoice_no",
      meta: { label: t`Invoice No` },
      header: t`Invoice No`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.InvoiceNo,
      cell: ({ row }) => {
        const invoice: InvoiceNoColumnProps["invoice"] = {
          id: row.original.outgoingInvoice.id,
          invoice_no: row.original.outgoingInvoice.invoice_no,
          has_proforma_payments:
            row.original.outgoingInvoice.has_proforma_payments,
          abraflexi_exported: row.original.outgoingInvoice.abraflexi_exported,
          xero_exported: row.original.outgoingInvoice.xero_exported,
          documentExports: row.original.outgoingInvoice.documentExports,
          emailLogs: row.original.outgoingInvoice.emailLogs,
          type: row.original.outgoingInvoice.type,
          proformaInvoicesPivot:
            row.original.outgoingInvoice.proformaInvoicesPivot.map(
              ({ invoice }) => invoice,
            ),
        };

        return <InvoiceNoColumn invoice={invoice} />;
      },
    },
    {
      id: "brand",
      meta: { label: t`Brand` },
      header: t`Brand`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.Brand,
      cell: ({ row }) => {
        const options = mapToOptions.brands(
          row.original.outgoingInvoice.brands,
        );

        return (
          <TruncatedTextWithTooltip
            text={
              <OptionsToLinks options={options} pathname="/brands/detail" />
            }
            tooltip={
              <OptionsToLinks
                options={options}
                pathname="/brands/detail"
                color="white"
              />
            }
          />
        );
      },
    },
    {
      id: "project",
      meta: { label: t`Project` },
      header: t`Project`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.Project,
      cell: ({ row }) => {
        const options = mapToOptions.projects(
          row.original.outgoingInvoice.projects,
        );

        return (
          <TruncatedTextWithTooltip
            text={
              <OptionsToLinks options={options} pathname="/projects/detail" />
            }
            tooltip={
              <OptionsToLinks
                options={options}
                pathname="/projects/detail"
                color="white"
              />
            }
          />
        );
      },
    },
    {
      id: "amount",
      meta: { label: t`Amount`, align: "right" },
      header: t`Amount`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.Amount,
      cell: ({ row }) => {
        return currency.formatByWorkspace(row.original.amount);
      },
    },
    {
      id: "issue_date",
      meta: { label: t`Issue date` },
      header: t`Issue date`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.IssueDate,
      cell: ({ row }) =>
        row.original.outgoingInvoice.issue_date
          ? formatDate(new Date(row.original.outgoingInvoice.issue_date))
          : null,
    },
    {
      id: "supply_date",
      meta: { label: t`Supply date` },
      header: t`Supply date`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.DateOfSupply,
      cell: ({ row }) =>
        row.original.outgoingInvoice.date_of_supply
          ? formatDate(new Date(row.original.outgoingInvoice.date_of_supply))
          : null,
    },
    {
      id: "due_date",
      meta: { label: t`Due date` },
      header: t`Due date`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.DueDate,
      cell: ({ row }) =>
        row.original.outgoingInvoice.due_date
          ? formatDate(new Date(row.original.outgoingInvoice.due_date))
          : null,
    },
    {
      id: "paid_at",
      meta: { label: t`Paid at` },
      header: t`Paid at`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.PaidAt,
      cell: ({ row }) => {
        const accountMoves = row.original.outgoingInvoice.accountMoves;
        const paid =
          Array.isArray(accountMoves) && accountMoves.length > 0
            ? accountMoves.reduce(
                (prev, curr) => prev + curr.amount_in_move_currency,
                0,
              )
            : undefined;

        return (
          <PaidAtCell
            paidAt={row.original.outgoingInvoice.paid_at}
            hasPayment={!!paid && paid !== 0}
          />
        );
      },
    },
    {
      id: "payment_method",
      meta: { label: t`Payment method` },
      header: t`Payment method`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.PaymentMethod,
      cell: ({ row }) =>
        row.original.outgoingInvoice.invoicePaymentMethod?.name,
    },
    {
      id: "note",
      meta: { label: t`Note` },
      header: t`Note`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.Note,
      cell: ({ row }) => {
        return (
          <TruncatedTextWithTooltip text={row.original.outgoingInvoice.note} />
        );
      },
    },
    {
      id: "internal_note",
      meta: { label: t`Internal note` },
      header: t`Internal note`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.InternalNote,
      cell: ({ row }) => {
        return (
          <TruncatedTextWithTooltip
            text={row.original.outgoingInvoice.internal_note}
          />
        );
      },
    },
    {
      id: "description",
      meta: { label: t`Description` },
      header: t`Description`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.Description,
      cell: ({ row }) => {
        return (
          <TruncatedTextWithTooltip
            text={row.original.outgoingInvoice.text_on_invoice}
          />
        );
      },
    },
    {
      id: "time_billing",
      meta: { label: t`Time billing` },
      header: t`Time billing`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.TimeBilling,
      cell: ({ row }) => {
        const timeBilling = row.original.outgoingInvoice.projectTimeBilling;
        return (
          <TimeBillingsColumn
            timeBillings={timeBilling ? [timeBilling] : []}
            sourceId={row.original.budget_item_id}
          />
        );
      },
    },
    {
      id: "locked_time_billing",
      meta: { label: t`Locked time billing` },
      header: t`Locked time billing`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.LockedTimeBilling,
      cell: ({ row }) => {
        const timeBilling = row.original.outgoingInvoice.projectTimeBilling;
        return (
          <LockedTimeBillingColumn
            timeBillings={timeBilling ? [timeBilling] : []}
          />
        );
      },
    },
    {
      id: "budget_item",
      meta: { label: t`Budget item` },
      header: t`Budget item`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.BudgetItem,
      cell: ({ row }) => {
        return (
          <TruncatedTextWithTooltip
            text={
              <BudgetItemColumn
                budgetItem={row.original.budgetItem}
                budgetItemType={row.original.budget_item_type}
              />
            }
            tooltip={
              <BudgetItemColumn
                budgetItem={row.original.budgetItem}
                budgetItemType={row.original.budget_item_type}
              />
            }
          />
        );
      },
    },
    {
      id: "legal_entity",
      meta: { label: t`Legal entity`, defaultHidden: hideLegalEntity },
      header: t`Legal entity`,
      orderBy: OiBudgetItemsTimeBillingOrderByColumn.LegalEntity,
      enableHiding: !hideLegalEntity,
      cell: ({ row }) => {
        const company = appStore.workspaceStore.companies.find(
          ({ id }) => id === row.original.outgoingInvoice.workspace_company_id,
        );

        return <TruncatedTextWithTooltip text={company?.internal_name} />;
      },
    },
    {
      id: ACTIONS_COLUMN_ID,
      meta: { label: t`Actions` },
    },
  ];
}
